import { Button } from "@/components/flexkit/Button";
import { TableHead, type HideableCellProps } from "@/components/flexkit/tables/Table";
import { cn } from "@/components/podkit/lib/cn";
import { IconArrowBottomTop } from "central-icons/IconArrowBottomTop";
import { IconArrowDown } from "central-icons/IconArrowDown";
import { IconArrowTopBottom } from "central-icons/IconArrowTopBottom";
import { IconArrowUp } from "central-icons/IconArrowUp";
import React, { useCallback, useMemo, type AriaAttributes } from "react";

export type TableSortOrder = "asc" | "desc" | null;

export type SortableTableHeadProps<T> = {
    columnName: T;
    sortOrder?: TableSortOrder;
    initialSortOrder?: TableSortOrder;
    /**
     * @param sortBy - The column name to sort by. Type is inherited from the columnName parameter or `null` if the sorting is removed.
     * @param sortOrder - The order to sort by.
     */
    onSort: (sortBy: T | null, sortOrder: TableSortOrder) => void;
} & HideableCellProps;

export const SortableTableHead = React.forwardRef(function SortableTableHead<T>(
    {
        columnName,
        sortOrder = null,
        initialSortOrder = "desc",
        children,
        onSort,
        className,
        ...props
    }: React.HTMLAttributes<HTMLTableCellElement> & SortableTableHeadProps<T>,
    ref: React.ForwardedRef<HTMLTableCellElement>,
) {
    const nextSortOrder = useMemo(() => {
        switch (sortOrder) {
            case "asc":
                return initialSortOrder === "desc" ? null : "desc";
            case "desc":
                return initialSortOrder === "asc" ? null : "asc";
            default:
                return initialSortOrder;
        }
    }, [sortOrder, initialSortOrder]);

    const handleClick = useCallback(() => {
        if (nextSortOrder === null) {
            onSort(null, null);
            return;
        }

        onSort(columnName, nextSortOrder);
    }, [onSort, columnName, nextSortOrder]);

    const ariaSort = useMemo<AriaAttributes["aria-sort"]>(() => {
        switch (sortOrder) {
            case "asc":
                return "ascending";
            case "desc":
                return "descending";
            default:
                return "none";
        }
    }, [sortOrder]);

    const ariaLabel = useMemo(() => {
        switch (nextSortOrder) {
            case "asc":
                return "Sort ascending";
            case "desc":
                return "Sort descending";
            default:
                return "Remove sorting";
        }
    }, [nextSortOrder]);

    const chevronIcon = useMemo(() => {
        switch (sortOrder) {
            case "asc":
                return <IconArrowUp size={16} />;
            case "desc":
                return <IconArrowDown size={16} />;
            default:
                if (initialSortOrder === "asc") {
                    return <IconArrowTopBottom size={16} />;
                } else {
                    return <IconArrowBottomTop size={16} />;
                }
        }
    }, [initialSortOrder, sortOrder]);

    const isRightAligned = className?.includes("text-right");

    return (
        <TableHead ref={ref} {...props} aria-sort={ariaSort} className={cn("px-2", className)} data-no-side-padding>
            <Button
                variant="ghost"
                onClick={handleClick}
                className={cn(
                    "group flex flex-row items-center gap-1 px-2 py-1 font-bold",
                    // For right-aligned columns, push the button to the right and reverse the
                    // icon/label order so the sort arrow sits to the left of the text. This keeps
                    // the label flush-right even when the arrow is hidden on unsorted columns.
                    // This is the standard pattern used by MUI (flexDirection: 'row-reverse' on
                    // right-aligned TableCell), GitHub Primer (flex-direction: row-reverse on
                    // TableSortButton[data-cell-align='end']), and IBM Carbon.
                    isRightAligned && "ml-auto flex-row-reverse",
                )}
                aria-label={ariaLabel}
                data-tracking-id-none
            >
                {children}
                <span
                    className={cn({
                        // Show chevron on hover for unsorted columns, always show for sorted columns
                        "invisible group-hover:visible group-focus-visible:visible": sortOrder === null,
                    })}
                >
                    {chevronIcon}
                </span>
            </Button>
        </TableHead>
    );
});
SortableTableHead.displayName = "SortableTableHead";
